import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";
import { classNames } from "@app/utils/classNames";

type ButtonVariant = "ghost" | "primary" | "link" | "accent" | "neutral" | "danger" | "secondary";
type ButtonSize = "md" | "sm" | "small";

type ButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: ReactNode;
    variant?: ButtonVariant;
    size?: ButtonSize;
  }
>;

export function Button({
  variant = "primary",
  icon,
  children,
  className,
  size = "md",
  ...rest
}: ButtonProps) {
  const normalizedSize = size === "small" ? "sm" : size;

  return (
    <button
      type="button"
      {...rest}
      className={classNames(
        "ui-button",
        `ui-button--${variant}`,
        normalizedSize === "sm" ? "ui-button--sm" : null,
        className,
      )}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </button>
  );
}

type LinkButtonProps = PropsWithChildren<
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: Extract<ButtonVariant, "link">;
    icon?: ReactNode;
  }
>;

export function LinkButton({
  children,
  className,
  icon,
  ...rest
}: LinkButtonProps) {
  return (
    <a {...rest} className={classNames("ui-button", "ui-button--link", className)}>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </a>
  );
}

